import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  ArrowDownTrayIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
} from '../../icons';
import { useTheme } from '../../contexts/ThemeContext';
import { adminAPI } from '../../services/adminAPI';
import TableColumnToggle from '../../components/layout/TableColumnToggle';
import { AUDIT_ACTION_TYPES, exportAuditLogCsv, filterAuditEvents } from '../../utils/auditLog';
import { formatDateTime } from '../../utils/transcriptionMeta';

const COLUMN_DEFS = [
  { key: 'created_at', label: 'Time' },
  { key: 'action_type', label: 'Action' },
  { key: 'actor_name', label: 'Actor' },
  { key: 'resource', label: 'Resource' },
  { key: 'details', label: 'Details' },
  { key: 'tenant', label: 'Tenant' },
];

const SuperAdminAuditLog = () => {
  const { dark } = useTheme();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [visibleKeys, setVisibleKeys] = useState(COLUMN_DEFS.map((c) => c.key));
  const [filters, setFilters] = useState({
    query: '',
    action_type: 'all',
    actor: '',
    date_from: '',
    date_to: '',
  });

  useEffect(() => {
    const load = async () => {
      try {
        const resp = await adminAPI.getAuditLog({});
        setEvents(resp.data?.data || resp.data || []);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const filtered = useMemo(() => filterAuditEvents(events, filters), [events, filters]);
  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  const pageRows = filtered.slice((page - 1) * perPage, page * perPage);

  useEffect(() => { setPage(1); }, [filters, perPage]);

  const show = (key) => visibleKeys.includes(key);

  const inputCls = `w-full px-3 py-2 text-sm rounded-lg border focus:outline-none focus:ring-2 focus:ring-matdash-primary/40
    ${dark ? 'bg-gray-800 border-gray-700 text-gray-100 placeholder-gray-500' : 'bg-white border-gray-200 text-gray-900'}`;
  const toolBtn = `inline-flex items-center justify-center h-9 px-3 rounded-lg border text-sm ${
    dark ? 'border-gray-700 text-gray-300 hover:bg-gray-800' : 'border-gray-200 text-gray-600 hover:bg-gray-50'
  }`;
  const dateCls = `px-3 h-9 py-1.5 text-sm rounded-lg border shrink-0 w-[9.75rem] focus:outline-none focus:ring-2 focus:ring-matdash-primary/40
    ${dark ? 'bg-gray-800 border-gray-700 text-gray-100' : 'bg-white border-gray-200 text-gray-900'}`;
  const dropdownPanel = 'absolute right-0 top-full pt-1 z-[80] hidden group-hover:block';
  const dropdownInner = `w-64 p-3 rounded-xl border shadow-lg space-y-3 ${
    dark ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'
  }`;

  const clearFilters = () => {
    setFilters({ query: '', action_type: 'all', actor: '', date_from: '', date_to: '' });
  };

  const activeFilterCount = [
    filters.action_type !== 'all',
    Boolean(filters.actor.trim()),
  ].filter(Boolean).length;

  const exportCsv = () => {
    exportAuditLogCsv(filtered, 'platform-audit-log.csv');
    toast.success('Exported as CSV');
  };

  const exportJson = () => {
    const blob = new Blob([JSON.stringify(filtered, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'platform-audit-log.json';
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Exported as JSON');
  };

  const pageNumbers = useMemo(() => {
    const max = Math.min(5, totalPages);
    return Array.from({ length: max }, (_, i) => i + 1);
  }, [totalPages]);

  return (
    <div>
      <div className={`md-card ${dark ? '!bg-gray-900 !border-gray-800' : ''}`}>
        <div className={`relative z-30 px-4 py-3 border-b flex flex-nowrap items-center gap-2 overflow-x-auto ${dark ? 'border-gray-800' : 'border-matdash-border'}`}>
          <div className="relative flex-1 min-w-[180px] max-w-sm shrink">
            <MagnifyingGlassIcon className="h-4 w-4 text-matdash-muted absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              value={filters.query}
              onChange={(e) => setFilters((f) => ({ ...f, query: e.target.value }))}
              placeholder="Search actions, actors, resources..."
              className={`${inputCls} pl-9 h-9 py-1.5`}
            />
          </div>

          <input
            type="date"
            value={filters.date_from}
            onChange={(e) => setFilters((f) => ({ ...f, date_from: e.target.value }))}
            className={dateCls}
            aria-label="From date"
          />
          <input
            type="date"
            value={filters.date_to}
            onChange={(e) => setFilters((f) => ({ ...f, date_to: e.target.value }))}
            className={dateCls}
            aria-label="To date"
          />

          <div className="ml-auto flex items-center gap-2 shrink-0">
            <div className="relative group">
              <button type="button" className={toolBtn} aria-label="Filters">
                <FunnelIcon className="h-4 w-4" />
                {activeFilterCount > 0 && (
                  <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-matdash-primary text-white text-[10px] font-semibold">
                    {activeFilterCount}
                  </span>
                )}
              </button>
              <div className={dropdownPanel}>
                <div className={dropdownInner}>
                  <div>
                    <label className="block text-xs font-medium mb-1 text-matdash-muted">Action</label>
                    <select
                      value={filters.action_type}
                      onChange={(e) => setFilters((f) => ({ ...f, action_type: e.target.value }))}
                      className={inputCls}
                    >
                      <option value="all">All actions</option>
                      {AUDIT_ACTION_TYPES.map((a) => <option key={a} value={a}>{a}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1 text-matdash-muted">Actor</label>
                    <input
                      value={filters.actor}
                      onChange={(e) => setFilters((f) => ({ ...f, actor: e.target.value }))}
                      placeholder="Filter by actor"
                      className={inputCls}
                    />
                  </div>
                  <button type="button" onClick={clearFilters} className="text-xs text-matdash-primary">
                    Clear filters
                  </button>
                </div>
              </div>
            </div>

            <TableColumnToggle
              columns={COLUMN_DEFS}
              visibleKeys={visibleKeys}
              onToggle={(key) => setVisibleKeys((keys) => (
                keys.includes(key) ? keys.filter((k) => k !== key) : [...keys, key]
              ))}
              label=""
            />

            <div className="relative group">
              <button type="button" className={toolBtn} aria-label="Export">
                <ArrowDownTrayIcon className="h-4 w-4" />
                <span className="ml-1.5 hidden sm:inline">Export</span>
              </button>
              <div className={dropdownPanel}>
                <div className={`w-44 py-1 rounded-xl border shadow-lg ${dark ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'}`}>
                  <button
                    type="button"
                    onClick={exportCsv}
                    className={`w-full text-left px-3 py-2 text-sm ${dark ? 'text-gray-200 hover:bg-gray-800' : 'text-matdash-dark hover:bg-gray-50'}`}
                  >
                    Export as CSV
                  </button>
                  <button
                    type="button"
                    onClick={exportJson}
                    className={`w-full text-left px-3 py-2 text-sm ${dark ? 'text-gray-200 hover:bg-gray-800' : 'text-matdash-dark hover:bg-gray-50'}`}
                  >
                    Export as JSON
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className={`px-4 py-2 border-b text-xs ${dark ? 'border-gray-800 text-gray-400' : 'border-matdash-border text-gray-500'}`}>
          Total events: {filtered.length} · Showing {filtered.length ? (page - 1) * perPage + 1 : 0} to {Math.min(page * perPage, filtered.length)}
        </div>

        <div className="overflow-x-auto">
          <table className="md-table min-w-[960px] w-full">
            <thead>
              <tr>
                {show('created_at') && <th className="whitespace-nowrap px-4 py-3">Time</th>}
                {show('action_type') && <th className="whitespace-nowrap px-4 py-3">Action</th>}
                {show('actor_name') && <th className="whitespace-nowrap px-4 py-3">Actor</th>}
                {show('resource') && <th className="whitespace-nowrap px-4 py-3">Resource</th>}
                {show('details') && <th className="whitespace-nowrap px-4 py-3">Details</th>}
                {show('tenant') && <th className="whitespace-nowrap px-4 py-3">Tenant</th>}
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={visibleKeys.length || 1} className="text-center text-matdash-muted py-10">
                    Loading audit log…
                  </td>
                </tr>
              )}
              {!loading && pageRows.length === 0 && (
                <tr>
                  <td colSpan={visibleKeys.length || 1} className="text-center text-matdash-muted py-10">
                    No audit events match your filters.
                  </td>
                </tr>
              )}
              {!loading && pageRows.map((row) => (
                <tr key={row.id}>
                  {show('created_at') && (
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-matdash-muted">
                      {formatDateTime(row.created_at)}
                    </td>
                  )}
                  {show('action_type') && (
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-matdash-primary-subtle text-matdash-primary">
                        {row.action_type}
                      </span>
                    </td>
                  )}
                  {show('actor_name') && (
                    <td className={`px-4 py-3 whitespace-nowrap text-sm ${dark ? 'text-gray-100' : 'text-matdash-dark'}`}>
                      {row.actor_name || '—'}
                    </td>
                  )}
                  {show('resource') && (
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-matdash-muted font-mono">
                      {row.resource || '—'}
                    </td>
                  )}
                  {show('details') && (
                    <td className={`px-4 py-3 text-sm max-w-md ${dark ? 'text-gray-200' : 'text-matdash-dark'}`}>
                      {row.details || '—'}
                    </td>
                  )}
                  {show('tenant') && (
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-matdash-muted">
                      {row.tenant_name || '—'}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className={`px-4 py-3 border-t flex flex-wrap items-center justify-between gap-3 ${dark ? 'border-gray-800' : 'border-matdash-border'}`}>
          <p className="text-xs text-matdash-muted mb-0">
            Showing {filtered.length === 0 ? 0 : (page - 1) * perPage + 1} to {Math.min(page * perPage, filtered.length)} of {filtered.length} results
          </p>
          <div className="flex items-center gap-2">
            <select
              value={perPage}
              onChange={(e) => setPerPage(Number(e.target.value))}
              className={`h-8 px-2 text-xs rounded-lg border ${dark ? 'bg-gray-800 border-gray-700 text-gray-200' : 'bg-white border-gray-200'}`}
            >
              {[10, 25, 50].map((n) => <option key={n} value={n}>{n} / page</option>)}
            </select>
            <button type="button" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} className="w-8 h-8 text-xs rounded border border-matdash-border disabled:opacity-40">‹</button>
            {pageNumbers.map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => setPage(n)}
                className={`w-8 h-8 text-xs rounded ${page === n ? 'bg-matdash-primary text-white' : 'border border-matdash-border text-matdash-muted'}`}
              >
                {n}
              </button>
            ))}
            <button type="button" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="w-8 h-8 text-xs rounded border border-matdash-border disabled:opacity-40">›</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuperAdminAuditLog;
